import React, { useState, useEffect, useMemo, useRef } from "react";
import { ArrowRight, ArrowLeft, Check, X, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { buildTicketTurns, pickTicketArc, MOOD_BADGE } from "@/utils/quiz";

const ARC_LABEL = {
  friendly: "Friendly",
  neutral: "Neutral",
  aggressive: "Aggressive",
  stress: "Escalating · stress test"
};

export default function TicketSimulation({
  question,
  index,
  total,
  answer,
  onAnswer,
  onNext,
  onBack
}) {
  // The ticket's mood arc is brushed once and persisted in the answer.
  const [arc, setArc] = useState(() => answer?.arc || pickTicketArc());
  const [picks, setPicks] = useState(() => answer?.picks ?? []);
  const [typing, setTyping] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef(null);

  const turns = useMemo(() => buildTicketTurns(question, arc), [question, arc]);

  useEffect(() => {
    onAnswer({ arc, picks });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arc, picks]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // How many customer messages are visible. During the "typing" beat the
  // next message stays hidden so the reply -> reaction flow feels live.
  const displayed = typing
    ? picks.length
    : Math.min(picks.length + 1, turns.length);
  const activeIndex = picks.length; // first unanswered turn
  const allAnswered =
    picks.length === turns.length &&
    picks.every((v) => v !== undefined && v !== null);
  const isCorrect = turns.every((t, i) => picks[i] === t.correctIndex);

  const handlePick = (i, oi) => {
    setPicks((prev) => {
      const next = [...prev];
      next[i] = oi;
      return next;
    });
    setSubmitted(false);
    if (i < turns.length - 1) {
      setTyping(true);
      timerRef.current = setTimeout(() => setTyping(false), 900);
    }
  };

  const initials = question.customer?.charAt(0) || "C";

  return (
    <div className="max-w-xl mx-auto px-6 py-10 sm:py-16">
      <div className="flex items-center justify-between mb-8">
        <span className="text-xs font-medium tracking-widest uppercase text-primary">
          Question {index + 1} of {total}
        </span>
        <span className="text-xs text-muted-foreground">Live ticket simulation</span>
      </div>

      <div className="w-full h-1.5 bg-muted rounded-full mb-8 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${((index + 1) / total) * 100}%` }}
        />
      </div>

      {/* Ticket header */}
      <div className="rounded-2xl border border-border bg-card p-5 mb-5">
        <div className="flex items-center justify-between mb-3 gap-2">
          <h2 className="text-base font-semibold text-foreground">
            {question.topic}
          </h2>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            From: {question.customer}
          </span>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {ARC_LABEL[arc]}
          </span>
        </div>
        <div className="rounded-lg bg-muted/50 border border-border p-3 flex gap-2">
          <BookOpen className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground/80">
              Knowledge base:{" "}
            </span>
            {question.knowledgeBase}
          </p>
        </div>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Reply to the customer — each reply triggers their next message. Once
        sent, it's sent.
      </p>

      {/* Chat thread */}
      <div className="space-y-3">
        {turns.map((t, i) => {
          const showCustomer = i < displayed;
          const agentPicked = picks[i] !== undefined && picks[i] !== null;
          const showOptions = i === activeIndex && !typing && !agentPicked;
          if (!showCustomer && !agentPicked && !showOptions) return null;

          return (
            <div key={i} className="space-y-3">
              {/* Customer bubble */}
              {showCustomer && (
                <div className="flex items-start gap-2.5 animate-chat-in">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-xs font-semibold">
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-medium text-foreground">
                        {question.customer}
                      </span>
                      <span
                        className={cn(
                          "text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded",
                          MOOD_BADGE[t.mood] || MOOD_BADGE.neutral
                        )}
                      >
                        {t.mood}
                      </span>
                      {t.kind === "out_of_scope" && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                          Unfamiliar
                        </span>
                      )}
                      {t.kind === "mind_change" && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">
                          Mind change
                        </span>
                      )}
                    </div>
                    <div className="rounded-2xl rounded-tl-sm bg-muted border border-border px-3.5 py-2.5 text-sm text-foreground/90 max-w-md">
                      {t.message}
                    </div>
                  </div>
                </div>
              )}

              {/* Agent bubble (the chosen reply) */}
              {agentPicked && (
                <div className="flex items-start gap-2.5 justify-end animate-chat-in">
                  <div className="min-w-0 max-w-md">
                    <div className="flex items-center gap-2 mb-1 justify-end">
                      <span className="text-xs font-medium text-muted-foreground">
                        You
                      </span>
                      {submitted &&
                        (picks[i] === t.correctIndex ? (
                          <Check className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <X className="w-3.5 h-3.5 text-rose-600" />
                        ))}
                    </div>
                    <div
                      className={cn(
                        "rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-sm border",
                        submitted
                          ? picks[i] === t.correctIndex
                            ? "bg-emerald-50 border-emerald-200 text-foreground/90"
                            : "bg-rose-50 border-rose-200 text-foreground/90"
                          : "bg-primary text-primary-foreground border-primary"
                      )}
                    >
                      {t.options[picks[i]]}
                    </div>
                    {submitted && picks[i] !== t.correctIndex && (
                      <p className="mt-1 text-xs text-emerald-600 text-right">
                        Best: {t.options[t.correctIndex]}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Reply options for the current turn */}
              {showOptions && (
                <div className="pl-10 space-y-2 animate-chat-in">
                  {t.options.map((opt, oi) => (
                    <button
                      key={oi}
                      type="button"
                      onClick={() => handlePick(i, oi)}
                      className="w-full text-left px-4 py-3 rounded-lg border border-border bg-card text-sm leading-relaxed transition-all hover:border-primary hover:bg-primary/5 hover:shadow-sm"
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Typing indicator before the next message appears */}
        {typing && picks.length < turns.length && (
          <div className="flex items-start gap-2.5 animate-chat-in">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center text-xs font-semibold">
              {initials}
            </div>
            <div className="rounded-2xl rounded-tl-sm bg-muted border border-border px-4 py-3 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:-0.3s]" />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce [animation-delay:-0.15s]" />
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/60 animate-bounce" />
            </div>
          </div>
        )}
      </div>

      {submitted && (
        <div
          className={cn(
            "mt-5 px-4 py-3 rounded-lg text-sm font-medium",
            isCorrect
              ? "bg-emerald-50 text-emerald-700"
              : "bg-rose-50 text-rose-700"
          )}
        >
          {isCorrect
            ? "Correct — you handled every message well."
            : "Not quite. Review the highlighted replies and submit again."}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
          disabled={index === 0}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowLeft className="mr-2 w-4 h-4" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setSubmitted(true)}
            variant="outline"
            className="rounded-full border-border"
            disabled={!allAnswered}
          >
            Submit
          </Button>
          <Button
            onClick={onNext}
            disabled={!allAnswered}
            className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6 disabled:opacity-30"
          >
            {index === total - 1 ? "Finish" : "Next"}
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}